'use client';
import { Alert, Box, Card, Fade, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TestSubmissionSummary from '@/components/test/test-submission-summary';
import { useTestResults } from '@/hooks';
import { useCourses } from '@/providers';

export default function TestResults() {
  const { t } = useTranslation();
  const { selectedCourse, courseStatus, isLoading } = useCourses();
  const { data: testResults } = useTestResults(selectedCourse?.id);
  const [indexActive, setIndexActive] = useState(0);
  const [highlightNewResult, setHighlightNewResult] = useState(false);
  const previousResultsCount = useRef<number>(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasMembership = courseStatus?.hasMembership;

  const triggerHighlight = useCallback((delay = 0) => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }

    highlightTimerRef.current = setTimeout(() => {
      setHighlightNewResult(true);
      highlightTimerRef.current = setTimeout(() => {
        setHighlightNewResult(false);
        highlightTimerRef.current = null;
      }, 2000);
    }, delay);
  }, []);

  useEffect(() => {
    if (testResults && testResults.length > 0) {
      const currentCount = testResults.length;

      if (currentCount > previousResultsCount.current && previousResultsCount.current > 0) {
        setIndexActive(0);

        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        triggerHighlight(1200);
      }

      previousResultsCount.current = currentCount;
    }
  }, [testResults, triggerHighlight]);

  useEffect(() => {
    if (!cardRef.current || !testResults?.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            if (indexActive === 0) {
              triggerHighlight(500);
            }
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [testResults, indexActive, triggerHighlight]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  if (isLoading || !selectedCourse) return <LinearProgress />;

  if (selectedCourse.type !== 'Test') return null;

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setIndexActive(newValue);
  };

  if (!hasMembership || !testResults?.length) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 2, userSelect: 'none' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
          {t('test.insight.title')}
        </Typography>
        <Alert severity="info" variant="outlined">
          Kaufe den Kurs und werte ihn aus, um hier Deine Ergebnisse zu sehen. Hierzu öffnest Du in
          der Lernansicht das Menü und klickst auf TEST AUSWERTEN. Bitte beachte die Hinweise!
        </Alert>
      </Card>
    );
  }

  return (
    <Card ref={cardRef} variant="outlined" sx={{ p: 3, mb: 2, userSelect: 'none' }}>
      {testResults.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={indexActive}
            onChange={handleChange}
            aria-label="Test Statistics Tabs"
            variant="scrollable">
            {testResults.map((result, tabIndex) => (
              <Tab
                key={result.createdAt}
                label={t('test.results.runTab', {
                  run: result.testRun ?? testResults.length - tabIndex,
                })}
                sx={{
                  transition: highlightNewResult
                    ? 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                    : 'all 1.2s cubic-bezier(0.165, 0.84, 0.44, 1)',
                  ...(tabIndex === 0 &&
                    highlightNewResult && {
                      backgroundColor: (theme) => `${theme.palette.primary.main}50`,
                      color: 'primary.contrastText',
                      borderRadius: 1,
                    }),
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}
      {testResults.map((result, index) => (
        <Fade in={indexActive === index} timeout={400} mountOnEnter unmountOnExit key={result._id}>
          <Box>
            <TestSubmissionSummary
              selectedResult={result}
              allResults={testResults}
              selectedIndex={index}
            />
          </Box>
        </Fade>
      ))}
    </Card>
  );
}
